import { Injectable } from '@angular/core';
import { AppStateService } from '../../../services/app-state.service';
import { PROJECT_CONSTANTS } from '../../../constants/proj.cnst';
import {
  getResourceActions,
  hasAction as hasPermissionAction,
  hasApplicationAccess as hasAppAccess,
  hasEditPermission as hasEditAction,
  hasReadPermission as hasReadAction,
  hasResourceAccess as hasResource,
  resolveUserPermissions
} from '../../../shared/permission.util';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  constructor(private readonly appStateService: AppStateService) { }

  private getPermissions(): Record<string, any> {
    return resolveUserPermissions(this.appStateService.getUserPermissions());
  }

  /**
   * Retrieves the list of permitted actions for a given resource key.
   * Returns an empty array if no actions are found for the resource.
   */
  getUserActions(resourceKey: string, applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): string[] {
    return getResourceActions(this.getPermissions(), resourceKey, applicationName);
  }

  hasApplicationAccess(applicationName: string): boolean {
    return hasAppAccess(this.getPermissions(), applicationName);
  }

  hasResourceAccess(resourceKey: string, applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): boolean {
    return hasResource(this.getPermissions(), resourceKey, applicationName);
  }

  hasAction(resourceKey: string, action: string, applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): boolean {
    return hasPermissionAction(this.getPermissions(), resourceKey, action, applicationName);
  }

  /**
   * Checks if the current user has 'EDIT' permission for the specified resource.
   * @param resourceKey - The key identifying the resource to check permissions for.
   */
  hasEditPermission(resourceKey: string, applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): boolean {
    return hasEditAction(this.getPermissions(), resourceKey, applicationName);
  }

  /**
   * Checks if the user has edit permission for at least one of the specified resource keys.
   * Returns true if any resource key grants edit permission, otherwise false.
   */
  hasAnyEditPermission(resourceKeys: string[], applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): boolean {
    return resourceKeys.some(key => this.hasEditPermission(key, applicationName));
  }

  hasReadPermission(resourceKey: string, applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): boolean {
    return hasReadAction(this.getPermissions(), resourceKey, applicationName);
  }

  hasAnyReadPermission(resourceKeys: string[], applicationName: string = PROJECT_CONSTANTS.APPLICATION_NAME): boolean {
    return resourceKeys.some((key) => this.hasReadPermission(key, applicationName));
  }
}
